import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import RemoveIcon from "@mui/icons-material/Remove";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  ChamberMemberProAccountAllocationTypeEnum,
  CmpEventSponsorshipRequestFieldsFragment,
  CmpEventSponsorshipRequestStatusEnum,
  CmpPaymentChoiceTypeEnum,
  useApproveCmpEventSponsorshipRequestMutation,
  useCancelCmpEventSponsorshipRequestMutation,
  useCreateCmpEventSponsorshipRequestMutation,
  useExportCmpEventSponsorsCsvLazyQuery,
  useGetChamberMemberProSettingsQuery,
  useGetCmpEventDetailQuery,
  useGetCmpEventSponsorshipRequestsQuery,
  useGetCmpInvoiceCreateLookupsQuery,
  useGetCmpManageEventsQuery,
  useRecordCmpEventSponsorshipManualPaymentMutation,
  useUpdateCmpEventSponsorshipRequestMutation,
} from "../../../../graphql/types";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { usePageHeaderOverride } from "../../../Layout/PageContainer";
import {
  stickyActionColumnCellSx,
  stickyActionColumnHeaderSx,
} from "../../../common/dashboard/DataTable";
import { FormPageShell } from "../../../common/dashboard/FormPageShell";
import { CMP_INVOICE_CREATE_LOOKUP_LIMIT } from "../../Invoicing/invoiceLookupConfig";
import {
  AttendeeAssignment,
  AttendeeAssignmentList,
  BillingDetails,
  blackButtonSx,
  blankAttendee,
  blankBillingDetails,
  ConfirmDialog,
  cpaGold,
  disabledFieldSx,
  downloadCsvFile,
  EventSalesFilterBar,
  EventSalesSection,
  EventSalesSortableHeader,
  EventSalesSortState,
  EventSelect,
  EventStatCardRow,
  formatCurrencyFromCents,
  getConfiguredInvoiceTaxRateLabel,
  getConfiguredInvoiceTaxRatePercentage,
  goldButtonSx,
  goldFieldSx,
  InvoiceLineItemTable,
  isPaidEventPaymentStatus,
  LineItemDeleteButton,
  ManualPaymentMethodOption,
  MemberBillingMode,
  MemberBillingSelector,
  MemberLookupOption,
  ProcessPaymentModal,
  ProcessPaymentSubmit,
  redButtonSx,
  ResultModal,
  splitName,
  toggleEventSalesSort,
  UpdateAttendeeNamesModal,
  useDownloadEventInvoicePdf,
} from "./eventSalesShared";
import EventDashboardPixelQa, {
  getDashboardPixelQaState,
} from "../pixelQa/EventDashboardPixelQa";

type Sponsorship = CmpEventSponsorshipRequestFieldsFragment;

const isInactiveSponsorship = (sponsorship: Sponsorship) =>
  sponsorship.status === CmpEventSponsorshipRequestStatusEnum.Canceled ||
  sponsorship.status === CmpEventSponsorshipRequestStatusEnum.Rejected;

const sponsorTicketStatusLabel = (sponsorship: Sponsorship) =>
  isPaidEventPaymentStatus(sponsorship.paymentStatus) ? "Issued" : "Ordered";

const isBlankAttendee = (attendee: AttendeeAssignment) =>
  !attendee.firstName.trim() &&
  !attendee.lastName.trim() &&
  !attendee.email.trim() &&
  !attendee.phone.trim();

// Mock's black pill "Preview Invoice" button (page-specific; the row buttons
// use the shared small-radius blackButtonSx per the pixel-parity sweep).
const blackPillButtonSx = {
  backgroundColor: "#111827",
  borderRadius: "9999px",
  fontWeight: 700,
  textTransform: "none" as const,
  "&:hover": { backgroundColor: "#000000" },
};

type SponsorSortColumn =
  | "companyName"
  | "invoiceStatus"
  | "sponsorshipType"
  | "tickets"
  | "ticketStatus";

const InvoiceManageSponsorshipSalesContent = (): React.ReactElement => {
  const setPageHeaderOverride = usePageHeaderOverride();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";

  const [selectedEventId, setSelectedEventId] = useState("");
  // Success + error feedback surfaces as popup modals (module policy
  // EVT-SALES-T-003) — never header Alerts/toasts.
  const [errorModal, setErrorModal] = useState<{
    title: string;
    message: string;
  } | null>(null);

  // Page header must match the mock verbatim (route config says
  // "Invoice & Manage Sponsorship Sales"; the mock reads "and", carries the
  // [sic] "sponsorship's" apostrophe and drops the trailing period).
  useEffect(() => {
    setPageHeaderOverride({
      title: "Invoice and Manage Sponsorship Sales",
      description:
        "Manually invoice event sponsorship's and manage all sponsorship invoices for events",
      withHeaderDivider: true,
    });
    return () => setPageHeaderOverride(null);
  }, [setPageHeaderOverride]);

  // ----- Queries -------------------------------------------------------------
  const { data: eventsData, loading: eventsLoading } = useGetCmpManageEventsQuery({
    variables: { input: { chamberId } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const events = useMemo(
    () => eventsData?.getCmpManageEvents.events || [],
    [eventsData]
  );

  useEffect(() => {
    if (!selectedEventId && events.length > 0) {
      setSelectedEventId(events[0].id);
    }
  }, [events, selectedEventId]);

  const { data: eventDetailData } = useGetCmpEventDetailQuery({
    variables: { input: { chamberId, eventId: selectedEventId } },
    skip: !chamberId || !selectedEventId,
    fetchPolicy: "cache-and-network",
  });
  const eventDetail = eventDetailData?.getCmpEventDetail.event || null;
  const sponsorshipPackages = useMemo(
    () => eventDetail?.sponsorshipPackages || [],
    [eventDetail]
  );
  const fallbackTicketTypeId = eventDetail?.tickets[0]?.id || "";

  const {
    data: sponsorshipsData,
    loading: sponsorshipsLoading,
    refetch: refetchSponsorships,
  } = useGetCmpEventSponsorshipRequestsQuery({
    variables: { input: { chamberId, eventId: selectedEventId } },
    skip: !chamberId || !selectedEventId,
    fetchPolicy: "cache-and-network",
  });
  const sponsorships = useMemo(
    () => sponsorshipsData?.getCmpEventSponsorshipRequests.sponsorshipRequests || [],
    [sponsorshipsData]
  );

  // Debounced server-side member search (EVT-BLAIR-SALES-002): the selector
  // debounces typing and hands us the term; we own the query at limit 1000.
  const [memberSearch, setMemberSearch] = useState("");
  const { data: lookupData, loading: lookupsLoading } =
    useGetCmpInvoiceCreateLookupsQuery({
      variables: {
        input: {
          chamberId,
          limit: CMP_INVOICE_CREATE_LOOKUP_LIMIT,
          search: memberSearch.trim() || undefined,
        },
      },
      skip: !chamberId,
      fetchPolicy: "cache-and-network",
    });
  const members: MemberLookupOption[] = useMemo(
    () => lookupData?.getCmpInvoiceCreateLookups.members || [],
    [lookupData]
  );

  const { data: settingsData } = useGetChamberMemberProSettingsQuery({
    variables: { chamberId },
    skip: !chamberId,
  });
  const manualPaymentMethods: ManualPaymentMethodOption[] = useMemo(
    () =>
      settingsData?.getChamberMemberProSettings.paymentsAndTaxes
        .manualPaymentMethods || [],
    [settingsData]
  );
  // Settings-driven tax label/rate, including split-rate chambers.
  const sponsorshipTaxSettings =
    settingsData?.getChamberMemberProSettings.paymentsAndTaxes.invoiceTaxSettings
      ?.sponsorshipInvoices;
  const taxRateLabel = getConfiguredInvoiceTaxRateLabel(sponsorshipTaxSettings);
  const taxRatePercentage = getConfiguredInvoiceTaxRatePercentage(
    sponsorshipTaxSettings
  );
  // Read-only ACC# auto-populates from the chamber's sponsorship sales
  // account allocation (mirrors B8's EventTicketSales pattern).
  const sponsorshipAccountNumber =
    settingsData?.getChamberMemberProSettings.paymentsAndTaxes.accountAllocations.find(
      (allocation) =>
        allocation.type ===
        ChamberMemberProAccountAllocationTypeEnum.SponsorshipSales
    )?.accountNumber || "";

  // ----- Mutations -----------------------------------------------------------
  const [createSponsorship, { loading: creating }] =
    useCreateCmpEventSponsorshipRequestMutation();
  const [approveSponsorship, { loading: approving }] =
    useApproveCmpEventSponsorshipRequestMutation();
  const [recordPayment, { loading: recordingPayment }] =
    useRecordCmpEventSponsorshipManualPaymentMutation();
  const [updateSponsorship, { loading: updatingSponsorship }] =
    useUpdateCmpEventSponsorshipRequestMutation();
  const [cancelSponsorship, { loading: cancelingSponsorship }] =
    useCancelCmpEventSponsorshipRequestMutation();
  const [exportSponsors, { loading: exportingSponsors }] =
    useExportCmpEventSponsorsCsvLazyQuery();
  const { downloadInvoicePdf, downloadingPdf } =
    useDownloadEventInvoicePdf(chamberId);

  const handleExportSponsors = async () => {
    if (!selectedEventId) {
      return;
    }
    const result = await exportSponsors({
      variables: { input: { chamberId, eventId: selectedEventId } },
    });
    const csv = result.data?.exportCmpEventSponsorsCsv;
    if (!csv) {
      if (result.error) {
        setErrorModal({
          title: "Something went wrong",
          message: result.error.message,
        });
      }
      return;
    }
    downloadCsvFile({
      csvContent: csv.csvContent,
      contentType: csv.contentType,
      filename: csv.filename,
    });
  };

  // ----- Analytics -----------------------------------------------------------
  const analytics = useMemo(() => {
    let sponsors = 0;
    let paid = 0;
    let unpaid = 0;
    let ticketsAllocated = 0;

    sponsorships.forEach((sponsorship) => {
      if (isInactiveSponsorship(sponsorship)) {
        return;
      }
      sponsors += 1;
      ticketsAllocated += sponsorship.includedAttendeeQuantity;
      if (isPaidEventPaymentStatus(sponsorship.paymentStatus)) {
        paid += 1;
      } else {
        unpaid += 1;
      }
    });

    return {
      sponsors,
      paid,
      unpaid,
      ticketsAllocated,
      revenueInCents: eventDetail?.sponsorshipRevenueInCents || 0,
    };
  }, [sponsorships, eventDetail]);

  // Availability (EVT-SALES-S-004): active (non-canceled/non-rejected)
  // requests hold a slot against each package's configured maxAvailable —
  // the same counting rule as the backend's public availability assert.
  const activePackageCounts = useMemo(() => {
    const counts = new Map<string, number>();
    sponsorships.forEach((sponsorship) => {
      if (isInactiveSponsorship(sponsorship)) {
        return;
      }
      counts.set(
        sponsorship.sponsorshipPackageId,
        (counts.get(sponsorship.sponsorshipPackageId) || 0) + 1
      );
    });
    return counts;
  }, [sponsorships]);

  const packageRemaining = (pkg: {
    id: string;
    maxAvailable?: number | null;
  }): number | null =>
    pkg.maxAvailable == null
      ? null
      : Math.max(0, pkg.maxAvailable - (activePackageCounts.get(pkg.id) || 0));

  // ----- Create invoice form -------------------------------------------------
  const [purchaserMode, setPurchaserMode] = useState<MemberBillingMode>("member");
  const [billing, setBilling] = useState<BillingDetails>(blankBillingDetails());
  const [packageId, setPackageId] = useState("");
  const [qty, setQty] = useState(1);
  const [taxExempt, setTaxExempt] = useState(false);
  const [attendees, setAttendees] = useState<AttendeeAssignment[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  // Field-level validation errors render in red under the offending
  // field/region (mirrors B8's fieldErrors approach) — no header banner.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [resultModal, setResultModal] = useState<{
    title: string;
    message: string;
  } | null>(null);

  const clearFieldErrors = (...keys: string[]) =>
    setFieldErrors((current) => {
      if (!keys.some((key) => current[key])) {
        return current;
      }
      const next = { ...current };
      keys.forEach((key) => delete next[key]);
      return next;
    });

  const selectedPackage = sponsorshipPackages.find((pkg) => pkg.id === packageId);
  const includedQuantity = selectedPackage?.includedAttendeeQuantity || 0;
  const selectedRemaining = selectedPackage
    ? packageRemaining(selectedPackage)
    : null;

  // Cap the QTY stepper at the selected level's remaining availability.
  useEffect(() => {
    if (!selectedPackage) {
      setQty(1);
      return;
    }
    if (selectedRemaining != null) {
      setQty((current) =>
        Math.min(Math.max(1, current), Math.max(1, selectedRemaining))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageId, selectedRemaining]);

  // Keep included-attendee rows in sync with the included ticket allocation.
  useEffect(() => {
    const target = Math.max(0, includedQuantity * qty);
    setAttendees((current) => {
      if (target === 0) {
        return [];
      }
      if (target <= current.length) {
        return current.slice(0, target);
      }
      const additions = Array.from({ length: target - current.length }, () =>
        blankAttendee()
      );
      return [...current, ...additions];
    });
  }, [includedQuantity, qty]);

  const unitPriceInCents = selectedPackage?.priceInCents || 0;
  const lineSubtotalInCents = unitPriceInCents * qty;
  const lineTaxInCents = taxExempt
    ? 0
    : Math.round((lineSubtotalInCents * taxRatePercentage) / 100);
  const lineTotalInCents = lineSubtotalInCents + lineTaxInCents;

  const includedTicket = eventDetail?.tickets.find(
    (ticket) =>
      ticket.id ===
      (selectedPackage?.includedAttendeeTicketTypeId || fallbackTicketTypeId)
  );

  const clearLineItem = () => {
    setPackageId("");
    setQty(1);
    setTaxExempt(false);
    clearFieldErrors("sponsorshipLevel");
  };

  const resetCreateForm = () => {
    setBilling(blankBillingDetails());
    clearLineItem();
    setFieldErrors({});
  };

  const handleBillingChange = (next: BillingDetails) => {
    setBilling(next);
    clearFieldErrors("member", "companyName", "billingEmail");
  };

  const handleModeChange = (mode: MemberBillingMode) => {
    setPurchaserMode(mode);
    setBilling(blankBillingDetails());
    clearFieldErrors("member", "companyName");
  };

  const handleSelectPackage = (id: string) => {
    setPackageId(id);
    clearFieldErrors("sponsorshipLevel");
  };

  const handleAttendeesChange = (next: AttendeeAssignment[]) => {
    setAttendees(next);
    clearFieldErrors("attendees");
  };

  // Field-level validation (mirrors B8) — errors render in red under the
  // offending field/region, never as a header banner.
  const validateCreate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (purchaserMode === "member" && !billing.selectedMemberId) {
      errors.member = "Select the member company to invoice.";
    }
    if (purchaserMode === "non-member" && !billing.customerName.trim()) {
      errors.companyName = "Enter the customer or company name to invoice.";
    }
    if (!selectedPackage) {
      errors.sponsorshipLevel = "Select a sponsorship level.";
    } else if (selectedRemaining != null && selectedRemaining <= 0) {
      errors.sponsorshipLevel = "This sponsorship level is sold out.";
    } else if (selectedRemaining != null && qty > selectedRemaining) {
      errors.sponsorshipLevel = `Only ${selectedRemaining} of this sponsorship level ${
        selectedRemaining === 1 ? "remains" : "remain"
      } available.`;
    }
    if (!billing.billingEmail.trim()) {
      errors.billingEmail = "Enter the billing contact email.";
    }
    const rowMissingName = attendees.some(
      (attendee) => !isBlankAttendee(attendee) && !attendee.firstName.trim()
    );
    if (rowMissingName) {
      errors.attendees =
        "Add a first name for each attendee row that has details.";
    }
    return errors;
  };

  const billingErrorList = [
    fieldErrors.member,
    fieldErrors.companyName,
    fieldErrors.billingEmail,
  ].filter(Boolean);

  const handleInvoiceSponsorship = async () => {
    const errors = validateCreate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    const selectedMember =
      purchaserMode === "member"
        ? members.find((member) => member.id === billing.selectedMemberId)
        : undefined;
    const purchaser = splitName(billing.billingContactName || billing.customerName);

    try {
      // One sponsorship request (and invoice) per QTY unit — the backend
      // models a sponsorship as a single request per slot.
      for (let unit = 0; unit < qty; unit += 1) {
        const attendeeChunk = attendees
          .slice(unit * includedQuantity, (unit + 1) * includedQuantity)
          .filter((attendee) => !isBlankAttendee(attendee));

        // Field-by-field input build — never spread Apollo query results.
        const input = {
          chamberId,
          eventId: selectedEventId,
          sponsorshipPackageId: packageId,
          selectedMemberCompanyId:
            purchaserMode === "member"
              ? selectedMember?.chamberMemberId || undefined
              : undefined,
          selectedMemberCompanyNameSnapshot:
            purchaserMode === "member"
              ? billing.customerName || undefined
              : undefined,
          sponsorDisplayName: billing.customerName,
          purchaserFirstName: purchaser.first || billing.customerName,
          purchaserLastName: purchaser.last || "-",
          purchaserEmail: billing.billingEmail,
          purchaserPhone: billing.billingPhone || undefined,
          includedAttendees:
            includedQuantity > 0 &&
            fallbackTicketTypeId &&
            attendeeChunk.length > 0
              ? attendeeChunk.map((attendee) => ({
                  firstName: attendee.firstName.trim(),
                  lastName: attendee.lastName.trim() || undefined,
                  email: attendee.email.trim() || undefined,
                  ticketTypeId:
                    selectedPackage?.includedAttendeeTicketTypeId ||
                    fallbackTicketTypeId,
                }))
              : undefined,
          // EVT-BLAIR-SALES-001 (contract B5): non-member purchasers submit a
          // billing snapshot instead of a member id (public billing input shape).
          billing:
            purchaserMode === "non-member"
              ? {
                  companyName: billing.customerName || undefined,
                  contactName: billing.billingContactName || undefined,
                  contactEmail: billing.billingEmail || undefined,
                  contactPhone: billing.billingPhone || undefined,
                  streetAddress: billing.streetAddress || undefined,
                  city: billing.city || undefined,
                  postalCode: billing.postalCode || undefined,
                }
              : undefined,
        };

        const created = await createSponsorship({ variables: { input } });

        const sponsorshipRequestId =
          created.data?.createCmpEventSponsorshipRequest?.sponsorshipRequest?.id;
        if (sponsorshipRequestId) {
          // Keep the create-then-approve(Manual) two-step: the invoice only
          // exists after approve.
          await approveSponsorship({
            variables: {
              input: {
                chamberId,
                eventId: selectedEventId,
                sponsorshipRequestId,
                paymentChoice: {
                  type: CmpPaymentChoiceTypeEnum.Manual,
                  emailCommunicationConsentConfirmed: true,
                },
                invoiceAccountCode: sponsorshipAccountNumber || undefined,
              },
            },
          });
        }
      }

      await refetchSponsorships();
      resetCreateForm();
      setResultModal({
        title: "Member Sponsor Invoiced",
        message:
          "Sponsorship Invoice has been generated and automatically sent to this member.",
      });
    } catch (error) {
      setErrorModal({
        title: "Something went wrong",
        message:
          error instanceof Error
            ? error.message
            : "Could not create the sponsorship invoice.",
      });
    }
  };

  // ----- Manage list: filters ------------------------------------------------
  const [search, setSearch] = useState("");
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState<
    "all" | "paid" | "unpaid"
  >("all");
  const [packageFilter, setPackageFilter] = useState("all");
  const [invoiceStatusMenuAnchor, setInvoiceStatusMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [packageMenuAnchor, setPackageMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [sort, setSort] =
    useState<EventSalesSortState<SponsorSortColumn> | null>(null);

  const filteredSponsorships = useMemo(() => {
    const term = search.trim().toLowerCase();
    return sponsorships.filter((sponsorship) => {
      if (isInactiveSponsorship(sponsorship)) {
        return false;
      }
      if (term) {
        const haystack = [
          sponsorship.sponsorDisplayName,
          sponsorship.selectedMemberCompanyNameSnapshot || "",
          sponsorship.purchaserFirstName,
          sponsorship.purchaserLastName,
          sponsorship.purchaserEmail,
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(term)) {
          return false;
        }
      }
      if (
        invoiceStatusFilter === "paid" &&
        !isPaidEventPaymentStatus(sponsorship.paymentStatus)
      ) {
        return false;
      }
      if (
        invoiceStatusFilter === "unpaid" &&
        isPaidEventPaymentStatus(sponsorship.paymentStatus)
      ) {
        return false;
      }
      if (
        packageFilter !== "all" &&
        sponsorship.sponsorshipPackageId !== packageFilter
      ) {
        return false;
      }
      return true;
    });
  }, [sponsorships, search, invoiceStatusFilter, packageFilter]);

  // Real asc/desc sorting on every column except Actions (the previous
  // decorative sort affordances were a lie in the UI — B9-06).
  const sortedSponsorships = useMemo(() => {
    if (!sort) {
      return filteredSponsorships;
    }
    const sortValue = (sponsorship: Sponsorship): string | number => {
      switch (sort.column) {
        case "companyName":
          return (
            sponsorship.selectedMemberCompanyNameSnapshot ||
            sponsorship.sponsorDisplayName
          ).toLowerCase();
        case "invoiceStatus":
          return isPaidEventPaymentStatus(sponsorship.paymentStatus)
            ? "paid"
            : "unpaid";
        case "sponsorshipType":
          return sponsorship.sponsorshipPackageNameSnapshot.toLowerCase();
        case "tickets":
          return sponsorship.includedAttendeeQuantity;
        case "ticketStatus":
          return sponsorTicketStatusLabel(sponsorship).toLowerCase();
        default:
          return "";
      }
    };
    const factor = sort.direction === "asc" ? 1 : -1;
    return [...filteredSponsorships].sort((a, b) => {
      const valueA = sortValue(a);
      const valueB = sortValue(b);
      if (valueA < valueB) {
        return -1 * factor;
      }
      if (valueA > valueB) {
        return 1 * factor;
      }
      return 0;
    });
  }, [filteredSponsorships, sort]);

  const toggleSort = (column: SponsorSortColumn) =>
    setSort((current) => toggleEventSalesSort(current, column));

  const renderSortableHeader = (label: string, column: SponsorSortColumn) => (
    <EventSalesSortableHeader
      label={label}
      column={column}
      sort={sort}
      onToggle={toggleSort}
    />
  );

  // ----- Process payment (Open Invoice) --------------------------------------
  const [paymentTarget, setPaymentTarget] = useState<Sponsorship | null>(null);

  const handleRecordPayment = async (payload: ProcessPaymentSubmit) => {
    if (!paymentTarget || !paymentTarget.invoice) {
      return;
    }
    try {
      await recordPayment({
        variables: {
          input: {
            chamberId,
            eventId: selectedEventId,
            sponsorshipRequestId: paymentTarget.id,
            invoiceId: paymentTarget.invoice.id,
            amountInCents: payload.amountInCents,
            manualPaymentMethodId: payload.manualPaymentMethodId,
            receivedAtUtcMilli: payload.receivedAtUtcMilli,
            reference: payload.reference || undefined,
            note: payload.note || undefined,
          },
        },
      });
      await refetchSponsorships();
      setPaymentTarget(null);
      setResultModal({
        title: "Your payment has been processed successfully!",
        message: "",
      });
    } catch (error) {
      setErrorModal({
        title: "Something went wrong",
        message:
          error instanceof Error
            ? error.message
            : "Could not process the payment.",
      });
    }
  };

  // ----- Cancel & Void (EVT-BLAIR-SALES-003) ----------------------------------
  const [cancelTarget, setCancelTarget] = useState<Sponsorship | null>(null);

  const handleCancelSponsorship = async () => {
    if (!cancelTarget) {
      return;
    }
    try {
      await cancelSponsorship({
        variables: {
          input: {
            chamberId,
            eventId: selectedEventId,
            sponsorshipRequestId: cancelTarget.id,
          },
        },
      });
      await refetchSponsorships();
      setCancelTarget(null);
      setResultModal({
        title: "Invoice Canceled",
        message: "Sponsorship canceled and its unpaid invoice voided.",
      });
    } catch (error) {
      setCancelTarget(null);
      setErrorModal({
        title: "Something went wrong",
        message:
          error instanceof Error
            ? error.message
            : "Could not cancel the sponsorship.",
      });
    }
  };

  // ----- Update included attendee names --------------------------------------
  const [editTarget, setEditTarget] = useState<Sponsorship | null>(null);
  const [editAttendees, setEditAttendees] = useState<
    { id: string; firstName: string; lastName: string; ticketTypeId: string; email: string }[]
  >([]);

  const openEditAttendees = (sponsorship: Sponsorship) => {
    setEditTarget(sponsorship);
    setEditAttendees(
      sponsorship.includedAttendees.map((attendee) => ({
        id: attendee.id,
        firstName: attendee.firstName,
        lastName: attendee.lastName || "",
        ticketTypeId: attendee.ticketTypeId,
        email: attendee.email || "",
      }))
    );
  };

  const handleSaveAttendeeNames = async () => {
    if (!editTarget) {
      return;
    }
    try {
      await updateSponsorship({
        variables: {
          input: {
            chamberId,
            eventId: selectedEventId,
            sponsorshipRequestId: editTarget.id,
            sponsorshipPackageId: editTarget.sponsorshipPackageId,
            selectedMemberCompanyId: editTarget.selectedMemberCompanyId || undefined,
            selectedMemberCompanyNameSnapshot:
              editTarget.selectedMemberCompanyNameSnapshot || undefined,
            sponsorDisplayName: editTarget.sponsorDisplayName,
            sponsorLogoUrl: editTarget.sponsorLogoUrl || undefined,
            sponsorLogoLink: editTarget.sponsorLogoLink || undefined,
            purchaserFirstName: editTarget.purchaserFirstName,
            purchaserLastName: editTarget.purchaserLastName,
            purchaserEmail: editTarget.purchaserEmail,
            purchaserPhone: editTarget.purchaserPhone || undefined,
            status: editTarget.status,
            includedAttendees: editAttendees.map((attendee) => ({
              firstName: attendee.firstName,
              lastName: attendee.lastName || undefined,
              email: attendee.email || undefined,
              ticketTypeId: attendee.ticketTypeId || fallbackTicketTypeId,
            })),
          },
        },
      });
      await refetchSponsorships();
      setEditTarget(null);
      setResultModal({
        title: "Attendee Names Updated",
        message: "Sponsor attendee names updated.",
      });
    } catch (error) {
      setErrorModal({
        title: "Something went wrong",
        message:
          error instanceof Error
            ? error.message
            : "Could not update attendee names.",
      });
    }
  };

  const selectedEvent = events.find((event) => event.id === selectedEventId);
  const eventName = selectedEvent?.title || "Event";

  const analyticsCards = [
    { label: "Event Sponsors", value: String(analytics.sponsors) },
    { label: "Sponsors Paid", value: String(analytics.paid) },
    { label: "Sponsors Unpaid", value: String(analytics.unpaid) },
    {
      label: "Event Sponsor Tickets Allocated",
      value: String(analytics.ticketsAllocated),
    },
    {
      label: "Event Sponsorship Revenue",
      value: formatCurrencyFromCents(analytics.revenueInCents),
    },
  ];

  const previewBillingAddress = [
    billing.streetAddress,
    billing.city,
    billing.postalCode,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    // Binding form-page system (side note 1): FormPageShell provides the
    // 1180px clamp + sr-only heading; the page header override above renders
    // the visible mock-verbatim title. No stepper, no bottom action bar (the
    // create buttons stay in-flow like B8).
    <FormPageShell
      headingId="sponsorship-sales-page"
      heading="Invoice and Manage Sponsorship Sales"
      contentClassName="space-y-6"
    >
      <EventSalesSection
        id="select-event"
        title="Select Event"
        description="Select event to invoice and manage sponsorship sales."
      >
        <div className="w-full md:w-1/2">
          <EventSelect
            options={events}
            value={selectedEventId}
            onChange={setSelectedEventId}
            loading={eventsLoading}
          />
        </div>
      </EventSalesSection>

      <EventSalesSection
        id="sponsorship-analytics"
        title={`${eventName} Sponsorship Analytics`}
        description="See a quick overview of your chamber sponsorship details for this event"
      >
        <EventStatCardRow cards={analyticsCards} hideIcons />
      </EventSalesSection>

      <EventSalesSection
        id="create-sponsorship-invoice"
        title="Create Sponsorship Invoice"
        description="Complete the information below, to create sponsorship invoice, and assign complimentary tickets if included in the sponsorship level."
      >
        <div className="space-y-5">
          <div>
            <MemberBillingSelector
              members={members}
              billing={billing}
              onChange={handleBillingChange}
              loading={lookupsLoading}
              mode={purchaserMode}
              onModeChange={handleModeChange}
              onSearchChange={setMemberSearch}
            />
            {billingErrorList.length > 0 ? (
              <div className="mt-2 space-y-0.5">
                {billingErrorList.map((message) => (
                  <p key={message} className="text-xs font-semibold text-red-600">
                    {message}
                  </p>
                ))}
              </div>
            ) : null}
          </div>

          <div>
            <h3 className="mb-2 text-base font-bold text-gray-950">
              Enter Invoice Details
            </h3>
            <InvoiceLineItemTable>
                    <TableCell className="!align-top">
                      {/* Configured sponsorship-level code (EVT-SALES-S-003) */}
                      <TextField
                        size="small"
                        placeholder="Code"
                        value={selectedPackage?.code || ""}
                        disabled
                        sx={{ width: 90, ...disabledFieldSx }}
                      />
                    </TableCell>
                    <TableCell className="!align-top">
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={packageId}
                        onChange={({ target }) =>
                          handleSelectPackage(target.value)
                        }
                        error={Boolean(fieldErrors.sponsorshipLevel)}
                        helperText={fieldErrors.sponsorshipLevel || undefined}
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (selected) => {
                            const pkg = sponsorshipPackages.find(
                              (item) => item.id === String(selected || "")
                            );
                            if (pkg) {
                              return pkg.name;
                            }
                            return (
                              <span className="text-[#8F969E]">
                                Select Sponsorship Level
                              </span>
                            );
                          },
                        }}
                        sx={goldFieldSx}
                      >
                        <MenuItem value="" disabled>
                          {sponsorshipPackages.length === 0
                            ? "No sponsorship levels configured"
                            : "Select Sponsorship Level"}
                        </MenuItem>
                        {sponsorshipPackages.map((pkg) => {
                          const remaining = packageRemaining(pkg);
                          // Exhausted levels are disabled (EVT-SALES-S-004)
                          // and say WHY (B9-08 polish: explicit suffix).
                          const soldOut =
                            remaining != null &&
                            remaining <= 0 &&
                            pkg.id !== packageId;
                          return (
                            <MenuItem value={pkg.id} key={pkg.id} disabled={soldOut}>
                              {soldOut ? `${pkg.name} (Sold Out)` : pkg.name}
                            </MenuItem>
                          );
                        })}
                      </TextField>
                    </TableCell>
                    <TableCell className="!align-top">
                      <div className="inline-flex items-center rounded border border-gray-300">
                        <IconButton
                          size="small"
                          aria-label="decrease quantity"
                          disabled={qty <= 1}
                          onClick={() => setQty((current) => Math.max(1, current - 1))}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <span className="w-8 text-center text-sm font-semibold text-gray-900">
                          {qty}
                        </span>
                        <IconButton
                          size="small"
                          aria-label="increase quantity"
                          disabled={
                            !selectedPackage ||
                            (selectedRemaining != null && qty >= selectedRemaining)
                          }
                          onClick={() =>
                            setQty((current) =>
                              selectedRemaining != null
                                ? Math.min(selectedRemaining, current + 1)
                                : current + 1
                            )
                          }
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </TableCell>
                    <TableCell className="!align-top">
                      {/* ACC# auto-populates read-only from the chamber's
                          SPONSORSHIP_SALES account allocation (grey disabled
                          box per the mock — mirrors B8). */}
                      <TextField
                        size="small"
                        placeholder="ACC#"
                        value={sponsorshipAccountNumber}
                        disabled
                        sx={{ width: 90, ...disabledFieldSx }}
                      />
                    </TableCell>
                    <TableCell className="!align-top">
                      <TextField
                        size="small"
                        placeholder="$0.00"
                        value={
                          selectedPackage
                            ? formatCurrencyFromCents(unitPriceInCents)
                            : ""
                        }
                        disabled
                        sx={{ width: 110, ...disabledFieldSx }}
                      />
                    </TableCell>
                    <TableCell className="!align-top">
                      <TextField
                        select
                        size="small"
                        value={taxExempt ? "exempt" : "tax"}
                        onChange={({ target }) =>
                          setTaxExempt(target.value === "exempt")
                        }
                        sx={{ width: 140, ...goldFieldSx }}
                      >
                        <MenuItem value="tax">
                          {taxRateLabel} ({taxRatePercentage}%)
                        </MenuItem>
                        <MenuItem value="exempt">Tax Exempt</MenuItem>
                      </TextField>
                    </TableCell>
                    <TableCell className="!align-top">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-gray-950">
                          {formatCurrencyFromCents(lineTotalInCents)}
                        </span>
                        {/* Mock trash token #F31723 (pixel-parity sweep). */}
                        <LineItemDeleteButton
                          onClick={clearLineItem}
                          ariaLabel="remove line item"
                        />
                      </div>
                    </TableCell>
            </InvoiceLineItemTable>
          </div>

          {attendees.length > 0 ? (
            <div className="rounded border border-gray-200 bg-white p-4">
              <p className="mb-3 text-sm text-gray-800">
                <span className="font-bold">Allocate Sponsor Tickets included</span>
                <span className="mx-1">•</span>
                <span className="font-bold">{attendees.length}</span> ticket(s)
                included with the above sponsorship add attendee names and emails
                and tickets will be emailed once sponsorship is paid.
              </p>
              <AttendeeAssignmentList
                attendees={attendees}
                onChange={handleAttendeesChange}
                attendeeFieldsOptional
              />
              {fieldErrors.attendees ? (
                <p className="mt-2 text-xs font-semibold text-red-600">
                  {fieldErrors.attendees}
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="space-y-4 pt-1">
            <div>
              <Button
                variant="contained"
                startIcon={<VisibilityOutlinedIcon />}
                onClick={() => setPreviewOpen(true)}
                sx={{ ...blackPillButtonSx, px: 3 }}
              >
                Preview Invoice
              </Button>
            </div>
            <div className="inline-flex flex-col">
              <Button
                variant="contained"
                disabled={creating || approving}
                onClick={handleInvoiceSponsorship}
                startIcon={
                  creating || approving ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : undefined
                }
                sx={{ ...goldButtonSx, minWidth: 220, px: 4 }}
              >
                Invoice Sponsorship
              </Button>
              <p className="mt-1 text-center text-xs font-bold text-gray-800">
                Invoice will be generated and sent.
              </p>
            </div>
          </div>
        </div>
      </EventSalesSection>

      {/* Export lives in its own right-aligned section, segregated from the
          create-invoice buttons (EVT-SALES-S-005). */}
      <div className="flex justify-end">
        <Button
          variant="contained"
          startIcon={<FileUploadOutlinedIcon />}
          disabled={exportingSponsors || !selectedEventId}
          onClick={handleExportSponsors}
          sx={goldButtonSx}
        >
          Export Sponsors List
        </Button>
      </div>

      <EventSalesSection
        id="sponsorship-level-filters"
        title="Sponsorship Level Filters"
        description="Use filters to easily sort information"
      >
        {/* Shared tall-control filter row (B8's 56px search + Button/Menu
            filter design, one implementation for both sales pages). */}
        <EventSalesFilterBar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by Attendee Name or Company Name"
          filters={[
            {
              label: "Filter by Invoice Status",
              active: invoiceStatusFilter !== "all",
              onOpen: setInvoiceStatusMenuAnchor,
            },
            {
              label: "Filter by Sponsorship Type",
              active: packageFilter !== "all",
              onOpen: setPackageMenuAnchor,
            },
          ]}
          onClearAllFilters={() => {
            setSearch("");
            setInvoiceStatusFilter("all");
            setPackageFilter("all");
          }}
        />

        <Menu
          anchorEl={invoiceStatusMenuAnchor}
          open={Boolean(invoiceStatusMenuAnchor)}
          onClose={() => setInvoiceStatusMenuAnchor(null)}
          PaperProps={{ sx: { borderRadius: "8px", minWidth: 240, py: 1 } }}
        >
          {[
            { value: "all", label: "All Invoice Statuses" },
            { value: "paid", label: "Paid" },
            { value: "unpaid", label: "Unpaid" },
          ].map((option) => (
            <MenuItem
              key={option.value}
              selected={invoiceStatusFilter === option.value}
              onClick={() => {
                setInvoiceStatusFilter(
                  option.value as "all" | "paid" | "unpaid"
                );
                setInvoiceStatusMenuAnchor(null);
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
        <Menu
          anchorEl={packageMenuAnchor}
          open={Boolean(packageMenuAnchor)}
          onClose={() => setPackageMenuAnchor(null)}
          PaperProps={{ sx: { borderRadius: "8px", minWidth: 240, py: 1 } }}
        >
          <MenuItem
            selected={packageFilter === "all"}
            onClick={() => {
              setPackageFilter("all");
              setPackageMenuAnchor(null);
            }}
          >
            All Sponsorship Types
          </MenuItem>
          {sponsorshipPackages.map((pkg) => (
            <MenuItem
              key={pkg.id}
              selected={packageFilter === pkg.id}
              onClick={() => {
                setPackageFilter(pkg.id);
                setPackageMenuAnchor(null);
              }}
            >
              {pkg.name}
            </MenuItem>
          ))}
        </Menu>
      </EventSalesSection>

      <EventSalesSection
        id="manage-sponsors-list"
        title="Manage Sponsors List"
        description={
          <>
            Update sponsor invoices status and update attendee names. Only
            sponsor invoices marked paid will have tickets sent to sponsors and
            show status as issued. Unpaid sponsor invoices will show ticket
            status as ordered and will not be issued until paid.{" "}
            <span className="font-bold text-gray-900">
              To update Sponsor invoice open invoice and click paid and tickets
              will be issued.
            </span>
          </>
        }
      >
        <TableContainer className="rounded border border-gray-200">
          <Table size="small" sx={{ minWidth: 980 }}>
            <TableHead className="bg-gray-50">
              <TableRow>
                <TableCell className="!font-bold">
                  {renderSortableHeader("Company Name", "companyName")}
                </TableCell>
                <TableCell className="!font-bold">
                  {renderSortableHeader(
                    "Update Invoice Status",
                    "invoiceStatus"
                  )}
                </TableCell>
                <TableCell className="!font-bold">
                  {renderSortableHeader("Sponsorship Type", "sponsorshipType")}
                </TableCell>
                <TableCell className="!font-bold" align="center">
                  {renderSortableHeader("No. of Tickets", "tickets")}
                </TableCell>
                <TableCell className="!font-bold">
                  {renderSortableHeader(
                    "Sponsorship/Ticket Status",
                    "ticketStatus"
                  )}
                </TableCell>
                {/* Actions is not sortable (mock). */}
                <TableCell
                  className="!font-bold"
                  align="right"
                  sx={{
                    ...stickyActionColumnHeaderSx,
                    minWidth: 300,
                    width: 300,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sponsorshipsLoading && sponsorships.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" className="!py-8">
                    <CircularProgress size={22} sx={{ color: cpaGold }} />
                  </TableCell>
                </TableRow>
              ) : null}
              {!sponsorshipsLoading && sortedSponsorships.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" className="!py-8 text-gray-500">
                    No sponsorship invoices for this event yet.
                  </TableCell>
                </TableRow>
              ) : null}
              {sortedSponsorships.map((sponsorship) => {
                const paid = isPaidEventPaymentStatus(sponsorship.paymentStatus);
                const purchaserName = `${sponsorship.purchaserFirstName} ${
                  sponsorship.purchaserLastName === "-"
                    ? ""
                    : sponsorship.purchaserLastName
                }`.trim();
                const companyName =
                  sponsorship.selectedMemberCompanyNameSnapshot ||
                  sponsorship.sponsorDisplayName;
                return (
                  <TableRow key={sponsorship.id}>
                    <TableCell>
                      <div className="font-bold text-gray-900">
                        {purchaserName || companyName}
                      </div>
                      <div className="text-xs text-gray-500">{companyName}</div>
                    </TableCell>
                    <TableCell>
                      <div
                        className={`text-sm font-bold ${
                          paid ? "text-green-600" : "text-gray-950"
                        }`}
                      >
                        {paid ? "Paid" : "Unpaid"}
                      </div>
                      {sponsorship.invoice ? (
                        // Small-radius rect per the mock (pixel-parity
                        // sweep) — matches B8's Open Invoice button.
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => setPaymentTarget(sponsorship)}
                          sx={{ ...blackButtonSx, mt: 0.5 }}
                        >
                          Open Invoice
                        </Button>
                      ) : null}
                    </TableCell>
                    <TableCell>{sponsorship.sponsorshipPackageNameSnapshot}</TableCell>
                    <TableCell align="center" className="!font-bold">
                      {sponsorship.includedAttendeeQuantity}
                    </TableCell>
                    <TableCell>{sponsorTicketStatusLabel(sponsorship)}</TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...stickyActionColumnCellSx,
                        minWidth: 300,
                        width: 300,
                      }}
                    >
                      <div className="flex flex-wrap justify-end gap-2">
                        {sponsorship.invoice ? (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={downloadingPdf}
                            onClick={() =>
                              downloadInvoicePdf(sponsorship.invoice?.id)
                            }
                            sx={{ textTransform: "none" }}
                          >
                            Invoice PDF
                          </Button>
                        ) : null}
                        {/* Small-radius rect per the mock (pixel-parity
                            sweep) — matches B8. */}
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<EditOutlinedIcon />}
                          onClick={() => openEditAttendees(sponsorship)}
                          sx={goldButtonSx}
                        >
                          Update Attendee Name
                        </Button>
                        {/* Contextual-action matrix (ACTION_MENU_MATRIX
                            'Ticket / Sponsorship Sales'): Cancel & Void is
                            HIDDEN on paid/complimentary rows, not disabled. */}
                        {paid ? null : (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => setCancelTarget(sponsorship)}
                            sx={redButtonSx}
                          >
                            Cancel &amp; Void Invoice
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </EventSalesSection>

      {/* Pre-invoice preview (Preview Invoice) */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent className="relative">
          <IconButton
            size="small"
            onClick={() => setPreviewOpen(false)}
            className="!absolute right-2 top-2 text-gray-400"
            aria-label="close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <h3 className="text-lg font-bold text-gray-950">Preview Invoice</h3>

          <p className="mt-3 text-xs font-bold uppercase text-gray-500">Bill To</p>
          <p className="text-sm font-bold text-gray-900">
            {billing.customerName || "—"}
          </p>
          {billing.billingContactName ? (
            <p className="text-sm text-gray-700">
              Attn: {billing.billingContactName}
            </p>
          ) : null}
          {previewBillingAddress ? (
            <p className="text-sm text-gray-700">{previewBillingAddress}</p>
          ) : null}
          {billing.billingEmail ? (
            <p className="text-sm text-gray-700">{billing.billingEmail}</p>
          ) : null}
          {billing.billingPhone ? (
            <p className="text-sm text-gray-700">{billing.billingPhone}</p>
          ) : null}

          <div className="mt-4 overflow-hidden rounded border border-gray-200">
            <div className="grid grid-cols-[88px_minmax(0,1fr)_60px_110px_100px_110px] bg-[#2f7de1] px-3 py-2 text-xs font-bold uppercase text-white">
              <span>Code</span>
              <span>Description</span>
              <span className="text-center">Qty</span>
              <span className="text-right">Unit Price</span>
              <span className="text-right">Tax</span>
              <span className="text-right">Total</span>
            </div>
            <div className="divide-y divide-gray-100">
              <div className="grid grid-cols-[88px_minmax(0,1fr)_60px_110px_100px_110px] items-center px-3 py-3 text-sm">
                <span className="font-semibold text-gray-900">
                  {selectedPackage?.code || "—"}
                </span>
                <span className="text-gray-800">
                  {selectedPackage?.name || "Select Sponsorship Level"}
                </span>
                <span className="text-center text-gray-700">{qty}</span>
                <span className="text-right text-gray-700">
                  {formatCurrencyFromCents(unitPriceInCents)}
                </span>
                <span className="text-right text-gray-700">
                  {formatCurrencyFromCents(lineTaxInCents)}
                </span>
                <span className="text-right font-semibold text-gray-900">
                  {formatCurrencyFromCents(lineTotalInCents)}
                </span>
              </div>
              {attendees.length > 0 && includedTicket ? (
                <div className="grid grid-cols-[88px_minmax(0,1fr)_60px_110px_100px_110px] items-center px-3 py-3 text-sm">
                  <span className="font-semibold text-gray-900">
                    {includedTicket.code || "—"}
                  </span>
                  <span className="text-gray-800">{includedTicket.name}</span>
                  <span className="text-center text-gray-700">
                    {attendees.length}
                  </span>
                  <span className="text-right text-gray-700">
                    {formatCurrencyFromCents(0)}
                  </span>
                  <span className="text-right text-gray-700">
                    {formatCurrencyFromCents(0)}
                  </span>
                  <span className="text-right font-semibold text-gray-900">
                    {formatCurrencyFromCents(0)}
                  </span>
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-3 flex justify-end">
            <div className="w-full max-w-xs space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-semibold text-gray-900">
                  {formatCurrencyFromCents(lineSubtotalInCents)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">
                  {taxRateLabel} ({taxExempt ? 0 : taxRatePercentage}%)
                </span>
                <span className="font-semibold text-gray-900">
                  {formatCurrencyFromCents(lineTaxInCents)}
                </span>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-2">
                <span className="font-bold uppercase text-gray-950">Total Due</span>
                <span className="font-bold text-gray-950">
                  {formatCurrencyFromCents(lineTotalInCents)}
                </span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Process payment (Open Invoice) */}
      <ProcessPaymentModal
        open={Boolean(paymentTarget)}
        customerName={
          paymentTarget
            ? paymentTarget.selectedMemberCompanyNameSnapshot ||
              paymentTarget.sponsorDisplayName
            : ""
        }
        invoiceNumber={paymentTarget?.invoice?.invoiceNumber || ""}
        amountDueInCents={paymentTarget?.invoice?.amountDueInCents || 0}
        invoiceLineItems={paymentTarget?.invoice?.lineItems || []}
        subtotalAmountInCents={paymentTarget?.invoice?.subtotalAmountInCents}
        taxAmountInCents={paymentTarget?.invoice?.taxAmountInCents}
        totalAmountInCents={paymentTarget?.invoice?.totalAmountInCents}
        paymentMethods={manualPaymentMethods}
        loading={recordingPayment}
        onSubmit={handleRecordPayment}
        onClose={() => setPaymentTarget(null)}
        onPrintPdf={
          paymentTarget?.invoice
            ? () => downloadInvoicePdf(paymentTarget.invoice?.id)
            : undefined
        }
        printLoading={downloadingPdf}
      />

      {/* Cancel & Void confirmation (EVT-BLAIR-SALES-003) */}
      <ConfirmDialog
        open={Boolean(cancelTarget)}
        tone="danger"
        title="Are you sure you want to cancel this sponsorship and void its invoice?"
        confirmLabel="Proceed"
        cancelLabel="Cancel"
        loading={cancelingSponsorship}
        onConfirm={handleCancelSponsorship}
        onCancel={() => setCancelTarget(null)}
      />

      {/* Update included attendee names (shared modal, "numbered" variant
          preserves this page's existing DOM) */}
      <UpdateAttendeeNamesModal
        open={Boolean(editTarget)}
        title="Update Attendee Name"
        attendees={editAttendees}
        onAttendeeChange={(id, patch) =>
          setEditAttendees((current) =>
            current.map((item) =>
              item.id === id ? { ...item, ...patch } : item
            )
          )
        }
        onClose={() => setEditTarget(null)}
        onSave={handleSaveAttendeeNames}
        saving={updatingSponsorship}
        variant="numbered"
        emptyMessage="This sponsorship has no included tickets to assign."
      />

      <ResultModal
        open={Boolean(resultModal)}
        title={resultModal?.title || ""}
        message={resultModal?.message}
        onClose={() => setResultModal(null)}
      />

      {/* API errors surface here as a popup, never a header toast/Alert. */}
      <ResultModal
        open={Boolean(errorModal)}
        success={false}
        title={errorModal?.title || ""}
        message={errorModal?.message}
        onClose={() => setErrorModal(null)}
      />
    </FormPageShell>
  );
};

const InvoiceManageSponsorshipSales = (): React.ReactElement => {
  const location = useLocation();
  const pixelQaState = getDashboardPixelQaState(
    location.search,
    "sponsorship-sales"
  );

  if (pixelQaState) {
    return <EventDashboardPixelQa state={pixelQaState} />;
  }

  return <InvoiceManageSponsorshipSalesContent />;
};

export default InvoiceManageSponsorshipSales;
